import React from 'react';
import {
  StarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
  ClockIcon,
} from '@heroicons/react/24/solid';
import { getEventPlaceholder } from '../../utils/eventPlaceholder';

const Slide = ({ event, formatEventDate, normalizeKey, onOpenModal, t }) => {
  if (!event) return null;
  return (
    <div className="relative h-full w-full cursor-pointer" onClick={() => onOpenModal(event)}>
      <img
        src={event.photoUrl || getEventPlaceholder(event.category)}
        className="w-full h-full object-contain"
        decoding="async"
        alt={event.title || ''}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-16 lg:p-24">
        <span className="inline-block px-3 py-1 rounded-md text-[10px] md:text-sm font-black uppercase mb-2 md:mb-4 bg-yellow-400 text-black">
          {t(`events.event-management.event-type.${normalizeKey(event.category)}`)}
        </span>
        <h3 className="text-xl sm:text-2xl md:text-5xl lg:text-6xl font-black text-white mb-2 md:mb-4 leading-tight line-clamp-2">
          {event.title}
        </h3>
        <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-6 text-white text-xs sm:text-sm md:text-lg font-medium mb-3 md:mb-5">
          <span className="flex items-center gap-1 truncate"><MapPinIcon className="h-4 w-4 text-yellow-400 flex-shrink-0" /> {event.provider?.businessName}</span>
          <span className="text-yellow-400 font-bold flex items-center gap-1">
            <ClockIcon className="h-3.5 w-3.5 flex-shrink-0" />
            {formatEventDate(event.start, event.end)}
          </span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onOpenModal(event); }}
          className="bg-white text-gray-900 font-bold px-5 py-2.5 rounded-full hover:bg-gray-100 transition-colors shadow-lg text-sm min-h-[44px]"
        >
          {t('events.viewDetails')}
        </button>
      </div>
    </div>
  );
};

const FeaturedCarousel = ({
  starEvents,
  currentIndex,
  prevIndex,
  isAnimating,
  reducedMotion,
  onNext,
  onPrev,
  onSetIndex,
  onPause,
  onResume,
  touchStartX,
  formatEventDate,
  normalizeKey,
  onOpenModal,
  t,
}) => {
  if (starEvents.length === 0) return null;

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowLeft': e.preventDefault(); onPrev(); break;
      case 'ArrowRight': e.preventDefault(); onNext(); break;
      case 'Home': e.preventDefault(); onSetIndex(0); break;
      case 'End': e.preventDefault(); onSetIndex(starEvents.length - 1); break;
      default: break;
    }
  };

  const transitionClass = reducedMotion ? '' : 'transition-all duration-500 ease-out';

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label={t('events.featuredCarousel')}
      className="relative w-full overflow-hidden mb-8 md:mb-10 rounded-2xl md:rounded-3xl shadow-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d5cab]"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className="absolute top-4 left-4 md:top-6 md:left-6 z-10 flex items-center gap-2 bg-black/50 backdrop-blur-sm rounded-full pl-1.5 pr-4 py-1.5 md:pl-2 md:pr-5 md:py-2">
        <div className="p-1.5 md:p-2 bg-yellow-400 rounded-full shadow-lg">
          <StarIcon className="h-3.5 w-3.5 md:h-4 md:w-4 text-black" />
        </div>
        <h2 className="text-sm md:text-lg font-black text-white">
          {t('events.featured-events') || 'Featured'}
        </h2>
      </div>

      <div
        className="relative w-full bg-gray-900"
        onMouseEnter={onPause}
        onMouseLeave={onResume}
        onFocus={onPause}
        onBlur={onResume}
        onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return;
          const diff = touchStartX.current - e.changedTouches[0].clientX;
          if (Math.abs(diff) > 50) { diff > 0 ? onNext() : onPrev(); }
          touchStartX.current = null;
        }}
      >
        <button onClick={onPrev} className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-10 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors">
          <ChevronLeftIcon className="h-5 w-5 md:h-6 md:w-6" />
        </button>
        <button onClick={onNext} className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-10 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors">
          <ChevronRightIcon className="h-5 w-5 md:h-6 md:w-6" />
        </button>

        <div className="relative h-[220px] min-[380px]:h-[260px] sm:h-[360px] md:h-[50dvh] lg:max-h-[640px] w-full overflow-hidden flex items-center justify-center">
          {/* Outgoing slide */}
          {isAnimating && prevIndex !== currentIndex && (
            <div
              role="group"
              aria-roledescription="slide"
              aria-label={`${prevIndex + 1} / ${starEvents.length}`}
              className={`absolute inset-0 ${transitionClass}`}
              style={reducedMotion ? { opacity: 0 } : { opacity: 0, transform: 'translateX(-2%)' }}
            >
              <Slide event={starEvents[prevIndex]} formatEventDate={formatEventDate} normalizeKey={normalizeKey} onOpenModal={onOpenModal} t={t} />
            </div>
          )}

          {/* Incoming / current slide */}
          <div
            role="group"
            aria-roledescription="slide"
            aria-label={`${currentIndex + 1} / ${starEvents.length}`}
            className={`absolute inset-0 ${transitionClass}`}
            style={
              isAnimating && !reducedMotion
                ? { opacity: 1, transform: 'translateX(0)' }
                : { opacity: 1, transform: 'translateX(0)' }
            }
            key={currentIndex}
          >
            <Slide event={starEvents[currentIndex]} formatEventDate={formatEventDate} normalizeKey={normalizeKey} onOpenModal={onOpenModal} t={t} />
          </div>
        </div>

        {starEvents.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
            {starEvents.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); onSetIndex(i); }}
                className={`rounded-full transition-all min-h-[12px] ${i === currentIndex ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-white/40 hover:bg-white/60'}`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeaturedCarousel;
